import { Box, Burger, Drawer, Group, Skeleton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useAuth } from '../../lib/auth-context';
import { BrandLogoLink } from '../ui/BrandLogoLink';
import { HeaderShell } from '../ui/HeaderShell';
import { SignUpButton } from '../ui/SignUpButton';
import { AppNavTabs } from './AppNavTabs';
import { AppSidebar } from './AppSidebar';
import { NotificationsNavLink } from './NotificationBell';
import { UserMenu } from './UserMenu';

/**
 * AppHeader — slim sticky top band for the authenticated app shell.
 *
 * The primary navigation lives in `<AppSidebar>` on lg+. This header keeps
 * the chrome continuity (brand on the left, sticky-translucent backdrop) and
 * carries the identity surfaces on the right: notifications bell + avatar
 * menu for signed-in users, or the sign-up CTA for the unauthenticated
 * sliver that briefly hits authenticated routes (e.g. /events while
 * anonymous in the dual-shell pages). Sign-in link hidden for launch
 * (2026-08-26) — /sign-in stays reachable directly.
 *
 * Mobile (< lg): the burger opens a left drawer carrying the same AppSidebar
 * contents so the nav model is identical across breakpoints.
 */
export function AppHeader() {
  const [drawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const { user, signOut, isLoading } = useAuth();

  // AppHeader is the full-width top band above the sidebar + content. On lg+ it
  // owns the brand (the sidebar no longer repeats it) and carries the primary
  // nav tabs; below lg the burger opens the sidebar drawer and the brand sits
  // next to it for orientation.
  return (
    <>
      <HeaderShell clampContent={false}>
        <Group gap="sm" wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
          <Burger
            opened={drawerOpen}
            onClick={openDrawer}
            aria-label="Open navigation"
            size="sm"
            hiddenFrom="lg"
          />
          <BrandLogoLink />
        </Group>

        {/* Primary nav tabs — full-width strip on lg+ for signed-in users. */}
        {user !== null ? (
          <Box visibleFrom="lg" style={{ flex: 1, minWidth: 0, display: 'flex' }}>
            <AppNavTabs />
          </Box>
        ) : null}

        {/* Right side: identity + quick actions. While auth is resolving we
            render a neutral skeleton so visitors with a session don't briefly
            see the signed-out CTA — that was the source of the /browse
            "Sign in / Sign up" flicker that swapped to the avatar a moment
            later. */}
        <Group gap="sm" wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
          {isLoading ? (
            <AuthSkeleton />
          ) : user === null ? (
            <SignUpButton size="xs" />
          ) : (
            <>
              <NotificationsNavLink size="sm" />
              <UserMenu user={user} signOut={signOut} />
            </>
          )}
        </Group>
      </HeaderShell>

      {/* Mobile drawer — surfaces the AppSidebar so navigation is consistent
          with desktop. Drawer width matches the desktop sidebar (~280px on
          a phone). Tapping a nav item closes the drawer. */}
      <Drawer
        opened={drawerOpen}
        onClose={closeDrawer}
        position="left"
        size={280}
        withCloseButton={false}
        padding={0}
        styles={{
          body: { padding: 0, height: '100%' },
          content: { display: 'flex', flexDirection: 'column' },
        }}
      >
        {user !== null ? (
          <AppSidebar onNavigate={closeDrawer} showBrand />
        ) : (
          <Box p="md">
            <SignUpButton fullWidth onClick={closeDrawer} />
          </Box>
        )}
      </Drawer>
    </>
  );
}

function AuthSkeleton() {
  return (
    <Group gap={8} wrap="nowrap" aria-hidden>
      <Skeleton height={28} circle />
      <Skeleton height={28} circle />
    </Group>
  );
}
